import {
  Block,
  BlockFooter,
  Button,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Preloader,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { useEffect, useState } from 'react';
import {
  useRestaurant,
  useUpdateRestaurant,
} from '@/features/restaurant-setup/hooks/useRestaurantSetup';
import { useT } from '@/i18n';
import { MapboxLocationPicker, type LatLng } from '../components/MapboxLocationPicker';

// Slide-in editor (FRONTEND_PRACTICES Rule 12). Pins the restaurant's geographic
// location - the origin distance-based delivery pricing measures from.
export default function RestaurantLocationPage() {
  const t = useT();
  const lf = t.orderSetup.page.locationForm;
  const { data: restaurant } = useRestaurant();
  const update = useUpdateRestaurant();
  const [coords, setCoords] = useState<LatLng | null>(null);

  useEffect(() => {
    if (restaurant?.latitude != null && restaurant?.longitude != null) {
      setCoords({ latitude: restaurant.latitude, longitude: restaurant.longitude });
    }
  }, [restaurant?.latitude, restaurant?.longitude]);

  const back = () => f7.views.current.router.back();
  // Wipe the pin. Sending null clears the columns (the update use-case passes
  // null through); the origin-less state is what distance pricing reads as
  // "not pinned", so this genuinely removes the location, not just the local pin.
  const hasSavedLocation = restaurant?.latitude != null && restaurant?.longitude != null;
  const clear = () => {
    update.mutate(
      { latitude: null, longitude: null },
      {
        onSuccess: () => {
          setCoords(null);
          f7.toast.show({ text: lf.cleared, closeTimeout: 2000, position: 'bottom' });
        },
        onError: (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : lf.clearFailed),
      },
    );
  };
  const save = () => {
    if (!coords) {
      f7.dialog.alert(lf.tapPrompt);
      return;
    }
    update.mutate(
      { latitude: coords.latitude, longitude: coords.longitude },
      {
        onSuccess: () => {
          f7.toast.show({ text: lf.saved, closeTimeout: 2000, position: 'bottom' });
          back();
        },
        onError: (e: unknown) => f7.dialog.alert(e instanceof Error ? e.message : lf.saveFailed),
      },
    );
  };

  return (
    <Page name="restaurant-location" pageContent={false}>
      <Navbar title={t.orderSetup.page.restaurantLocation} backLink={t.back}>
        <NavRight>
          <F7Link onClick={save}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>
      <PageContent>
        {!restaurant ? (
          <Block className="text-align-center" style={{ paddingTop: 48 }}>
            <Preloader />
          </Block>
        ) : (
          <>
            <MapboxLocationPicker
              value={coords}
              onChange={setCoords}
              // Seed the search box with the address typed in the business profile
              // so the operator doesn't re-enter it. Auto-search only when there's
              // no saved pin yet (a pinned restaurant keeps its location).
              initialQuery={[restaurant.address, restaurant.postalCode, restaurant.city]
                .filter(Boolean)
                .join(', ')}
              autoSearch={!hasSavedLocation}
            />
            <BlockFooter>
              {lf.footer}
              {coords
                ? lf.currentCoords(coords.latitude.toFixed(5), coords.longitude.toFixed(5))
                : ''}
            </BlockFooter>
            {hasSavedLocation && (
              <Block>
                <Button colorTheme="red" onClick={clear} disabled={update.isPending}>
                  {lf.clear}
                </Button>
              </Block>
            )}
          </>
        )}
      </PageContent>
    </Page>
  );
}
